import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { Button, CTA, PageHero, Reveal, SectionIntro, useSeo } from "../components/Shared";
import { IMG, SERVICES } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FALLBACK_SERVICES = SERVICES.map((s, i) => ({ id: `static-${i}`, name: s.title, copy: s.copy }));

export default function Services() {
  useSeo(
    "Services & Divisions | Dishan International Group",
    "Business & brand consulting, corporate solutions, mental health & wellness and ventures, clarity for the next move."
  );
  const { hash } = useLocation();
  const [services, setServices] = useState(FALLBACK_SERVICES);
  useEffect(() => {
    axios.get(`${API}/services`).then((r) => { if (r.data.length) setServices(r.data); }).catch(() => {});
  }, []);
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    }
  }, [hash]);

  return (
    <>
      <PageHero
        eyebrow="Services & divisions"
        titleKey="services.hero.title" title="Clarity for the next move."
        subtitleKey="services.hero.subtitle" subtitle="From business strategy to human potential, our work is designed around the real challenge, not a pre-packaged answer."
      />
      <main>
        <section className="section" id="consulting">
          <div className="container">
            <div className="image-split reverse">
              <Reveal className="image-split-media"><img src={IMG.consulting} alt="Strategic consulting session" loading="lazy" /></Reveal>
              <Reveal delay={0.1}>
                <span className="eyebrow">Business & brand consulting</span>
                <h2>Strategy that earns momentum.</h2>
                <p>We work with founders and leadership teams on the decisions that shape growth, positioning, brand behaviour, marketing intelligence and the operating clarity to act on all three.</p>
                <Button variant="secondary">Book a Consultation</Button>
              </Reveal>
            </div>
            <div className="service-list">
              {services.map((s, i) => (
                <Reveal key={s.id} delay={i * 0.04} className="service-row">
                  <span>0{i + 1}</span>
                  <h3>{s.name}</h3>
                  <p>{s.copy}</p>
                  <Link to="/contact" aria-label={`Enquire about ${s.name}`} data-testid={`service-${i + 1}-link`}><ArrowUpRight /></Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
        <section className="section mist" id="corporate">
          <div className="container">
            <SectionIntro
              eyebrow="Corporate solutions"
              title="For organizations ready to move forward."
              copy="Business strategy, brand transformation, marketing advisory, leadership development and AI-led business transformation, shaped around your context."
              action={{ label: "Partner with Us", to: "/contact" }}
            />
            <div className="image-split">
              <Reveal className="image-split-media"><img src={IMG.corporate} alt="Corporate partnership" loading="lazy" /></Reveal>
              <Reveal delay={0.1} className="mini-grid">
                {["Business strategy", "Brand transformation", "Leadership development", "Marketing advisory", "AI-led transformation", "Human capability"].map((x) => (
                  <div className="mini-card" key={x}><Check size={16} /><span>{x}</span></div>
                ))}
              </Reveal>
            </div>
          </div>
        </section>
        <section className="section sage" id="wellness">
          <div className="container image-split reverse">
            <Reveal className="image-split-media"><img src={IMG.wellness} alt="Calm wellness consultation" loading="lazy" /></Reveal>
            <Reveal delay={0.1}>
              <span className="eyebrow">Mental health & wellness</span>
              <h2>A more respectful way to ask for support.</h2>
              <p>Explore counselling, psychotherapy, creative art & psychotherapy, wellness and image consultation through an empathetic, professional lens. No medical promises, just a thoughtful place to begin.</p>
              <Button variant="secondary">Explore Support</Button>
            </Reveal>
          </div>
        </section>
        <section className="section navy" id="ventures">
          <div className="container split-section">
            <Reveal>
              <span className="eyebrow on-dark">Brands & ventures</span>
              <h2>A connected portfolio, built with intent.</h2>
            </Reveal>
            <Reveal delay={0.1} className="large-copy">
              <p>Across culture, commerce, media, hospitality and events, the Dishan ecosystem incubates and partners with ventures that share one belief, growth should be intelligent, human and built to last.</p>
              <Button variant="ghost">Discuss a Partnership</Button>
            </Reveal>
          </div>
        </section>
        <CTA />
      </main>
    </>
  );
}
